import {
  useDisconnectProcoreMutation,
  useGetProcoreConnectionStatusQuery,
  useProcoreOAuthStartMutation,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { CheckIcon } from 'components/icons';
import React, { ReactElement, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Badge } from '@odin-labs/components';
import { useFlags } from 'featureFlags';

type StatusCellProps = {
  loading: boolean;
  connected: boolean;
  starting: boolean;
  disconnecting: boolean;
  onLink: () => void;
  onDisconnect: () => void;
};

function renderStatusCell({
  loading,
  connected,
  starting,
  disconnecting,
  onLink,
  onDisconnect,
}: StatusCellProps): ReactElement {
  if (loading) {
    return (
      <dd className="odin-mt-1 odin-text-sm odin-text-gray-500 sm:odin-mt-0">
        Loading…
      </dd>
    );
  }
  if (connected) {
    return (
      <dd className="odin-mt-1 odin-flex odin-items-center odin-gap-3 sm:odin-mt-0">
        <Badge color="green" className="odin-p-1 odin-pr-2">
          <CheckIcon className="odin-mr-2" /> Linked
        </Badge>
        <button
          type="button"
          onClick={onDisconnect}
          disabled={disconnecting}
          className="odin-text-sm odin-text-odin-danger odin-underline disabled:odin-opacity-50"
        >
          {disconnecting ? 'Disconnecting…' : 'Disconnect'}
        </button>
      </dd>
    );
  }
  return (
    <dd className="odin-mt-1 odin-text-sm odin-text-gray-900 sm:odin-mt-0">
      <button
        type="button"
        onClick={onLink}
        disabled={starting}
        className="odin-text-odin-primary odin-underline disabled:odin-opacity-50"
      >
        {starting ? 'Redirecting…' : 'Link Procore account'}
      </button>
    </dd>
  );
}

export function ExternalConnections(): ReactElement {
  const history = useHistory();
  const location = useLocation();
  const { procoreSelfServiceEnabled } = useFlags();

  // Surface a one-time success/failure alert when redirected back from the
  // exchange container, then strip the query params so a refresh doesn't
  // re-alert. The ref guard ensures we only fire once per mount even under
  // React strict mode's double-invoke and lets us honor exhaustive-deps
  // without re-alerting on every URL change.
  const alertedRef = useRef(false);
  useEffect(() => {
    if (alertedRef.current) return;
    const searchParams = new URLSearchParams(location.search);
    const linkedService = searchParams.get('linkedService');
    if (!linkedService) return;
    alertedRef.current = true;
    if (searchParams.has('succeeded')) {
      AlertService.alert(
        'info',
        `${linkedService} account linked successfully!`,
        null,
      );
    } else {
      AlertService.alert(
        'danger',
        `Could not link ${linkedService} account`,
        null,
      );
    }
    history.replace({ pathname: location.pathname, search: '' });
  }, [history, location.pathname, location.search]);

  const { data, loading, refetch } = useGetProcoreConnectionStatusQuery({
    fetchPolicy: 'cache-and-network',
  });
  const connected = data?.getProcoreConnectionStatus.connected ?? false;

  const [startOAuth, { loading: invokingStart }] =
    useProcoreOAuthStartMutation();
  const [disconnect, { loading: disconnecting }] = useDisconnectProcoreMutation(
    { onCompleted: () => refetch() },
  );

  const [starting, setStarting] = React.useState<boolean>(false);

  useEffect(() => {
    setStarting((prevState) => {
      return prevState || invokingStart;
    });
  }, [invokingStart]);

  const handleLink = async (): Promise<void> => {
    try {
      const result = await startOAuth();
      const authorizeUrl = result.data?.procoreOAuthStart.authorizeUrl;
      if (authorizeUrl) {
        window.location.assign(authorizeUrl);
      } else {
        AlertService.alert('danger', 'Could not start Procore link', null);
        setStarting(false);
      }
    } catch {
      AlertService.alert('danger', 'Could not start Procore link', null);
      setStarting(false);
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    try {
      await disconnect();
      AlertService.alert('info', 'Procore account disconnected', null);
    } catch {
      AlertService.alert(
        'danger',
        'Could not disconnect Procore account',
        null,
      );
    }
  };

  return (
    <div className="odin-bg-white odin-shadow odin-overflow-hidden sm:odin-rounded-lg">
      <div className="odin-px-4 odin-py-5 sm:odin-px-6">
        <h3 className="odin-text-lg odin-leading-6 odin-font-medium odin-text-gray-900">
          External connections
        </h3>
      </div>
      <div className="odin-border-t odin-border-gray-200 odin-px-4 odin-py-5 sm:odin-p-0">
        {procoreSelfServiceEnabled && (
          <div className="odin-px-4 odin-py-5 sm:odin-p-0">
            <dl className="sm:odin-divide-y sm:odin-divide-gray-200">
              <div className="odin-flex odin-flex-col sm:odin-flex-row odin-justify-between odin-p-4">
                <dt className="odin-text-sm odin-font-medium odin-text-gray-500">
                  Procore
                </dt>
                {renderStatusCell({
                  loading,
                  connected,
                  starting,
                  disconnecting,
                  onLink: handleLink,
                  onDisconnect: handleDisconnect,
                })}
              </div>
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}
